/**
 * Utilitários de documento da wiki: sumário (TOC), conversão para Markdown
 * e para texto puro — usados por copiar, exportar .md e navegação interna.
 */

const parse = (content) => {
  if (!content) return null;
  if (typeof content === 'string') {
    try { return JSON.parse(content); } catch { return null; }
  }
  return content;
};

export const slugifyHeading = (texto, indice) =>
  `sec-${indice}-${String(texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 40)}`;

const textoDoNo = (no) => {
  if (!no) return '';
  if (no.type === 'text') return no.text || '';
  if (!Array.isArray(no.content)) return '';
  return no.content.map(textoDoNo).join('');
};

/** Lista de headings do documento, para montar o sumário lateral. */
export const extrairSumario = (content) => {
  const doc = parse(content);
  if (!doc || !Array.isArray(doc.content)) return [];

  return doc.content
    .filter((no) => no.type === 'heading')
    .map((no, i) => {
      const texto = textoDoNo(no).trim();
      return texto ? { id: slugifyHeading(texto, i), texto, nivel: no.attrs?.level || 1 } : null;
    })
    .filter(Boolean);
};

// ---------------------------------------------------------------- Markdown

const aplicarMarcas = (texto, marks = []) => {
  if (!texto) return '';
  let saida = texto;

  // código primeiro: o conteúdo dentro de crase não recebe outras marcas
  if (marks.some((m) => m.type === 'code')) return `\`${saida}\``;

  if (marks.some((m) => m.type === 'bold')) saida = `**${saida}**`;
  if (marks.some((m) => m.type === 'italic')) saida = `*${saida}*`;
  if (marks.some((m) => m.type === 'strike')) saida = `~~${saida}~~`;

  const link = marks.find((m) => m.type === 'link');
  if (link?.attrs?.href) saida = `[${saida}](${link.attrs.href})`;

  return saida;
};

const inlineParaMarkdown = (nos = []) =>
  nos.map((no) => {
    if (no.type === 'text') return aplicarMarcas(no.text || '', no.marks || []);
    if (no.type === 'hardBreak') return '\n';
    if (no.type === 'image') return `![${no.attrs?.alt || ''}](${no.attrs?.src || ''})`;
    return textoDoNo(no);
  }).join('');

const celulaParaTexto = (celula) =>
  (celula.content || []).map((p) => inlineParaMarkdown(p.content || [])).join(' ').trim();

const tabelaParaMarkdown = (no) => {
  const linhas = (no.content || []).map((linha) => (linha.content || []).map(celulaParaTexto));
  if (!linhas.length) return '';

  const colunas = Math.max(...linhas.map((l) => l.length));
  const normalizar = (l) => Array.from({ length: colunas }, (_, i) => l[i] ?? '');

  const [cabecalho, ...corpo] = linhas;
  const saida = [
    `| ${normalizar(cabecalho).join(' | ')} |`,
    `| ${Array(colunas).fill('---').join(' | ')} |`,
    ...corpo.map((l) => `| ${normalizar(l).join(' | ')} |`),
  ];

  return saida.join('\n');
};

const listaParaMarkdown = (no, nivel = 0, ordenada = false) => {
  const recuo = '  '.repeat(nivel);

  return (no.content || []).map((item, i) => {
    const marcador = ordenada ? `${i + 1}.` : '-';
    const caixa = no.type === 'taskList' ? (item.attrs?.checked ? '[x] ' : '[ ] ') : '';

    const partes = [];
    (item.content || []).forEach((filho, idx) => {
      if (['bulletList', 'orderedList', 'taskList'].includes(filho.type)) {
        partes.push(listaParaMarkdown(filho, nivel + 1, filho.type === 'orderedList'));
      } else {
        const texto = blocoParaMarkdown(filho);
        partes.push(idx === 0 ? `${recuo}${marcador} ${caixa}${texto}` : `${recuo}  ${texto}`);
      }
    });

    return partes.join('\n');
  }).join('\n');
};

function blocoParaMarkdown(no) {
  if (!no) return '';

  switch (no.type) {
    case 'heading':
      return `${'#'.repeat(no.attrs?.level || 1)} ${inlineParaMarkdown(no.content)}`;
    case 'paragraph':
      return inlineParaMarkdown(no.content);
    case 'bulletList':
      return listaParaMarkdown(no, 0, false);
    case 'orderedList':
      return listaParaMarkdown(no, 0, true);
    case 'taskList':
      return listaParaMarkdown(no, 0, false);
    case 'blockquote':
      return (no.content || [])
        .map(blocoParaMarkdown)
        .join('\n\n')
        .split('\n')
        .map((l) => `> ${l}`)
        .join('\n');
    case 'codeBlock':
      return `\`\`\`${no.attrs?.language || ''}\n${textoDoNo(no)}\n\`\`\``;
    case 'horizontalRule':
      return '---';
    case 'pageBreak':
      // marcador reconhecido na volta (ver markdown.js) e invisível ao renderizar
      return '<!-- quebra-de-pagina -->';
    case 'table':
      return tabelaParaMarkdown(no);
    case 'image':
      return `![${no.attrs?.alt || ''}](${no.attrs?.src || ''})`;
    case 'htmlBlock':
      return no.attrs?.html || textoDoNo(no);
    case 'youtube':
      return no.attrs?.src || '';
    default:
      return inlineParaMarkdown(no.content) || textoDoNo(no);
  }
}

/** Documento TipTap -> Markdown (para copiar e exportar .md). */
export const docParaMarkdown = (content) => {
  const doc = parse(content);
  if (!doc || !Array.isArray(doc.content)) return '';

  return doc.content
    .map(blocoParaMarkdown)
    .filter((b) => b !== null && b !== undefined)
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/** Documento TipTap -> texto puro (sem sintaxe), para colar em chat/e-mail. */
export const docParaTexto = (content) => {
  const doc = parse(content);
  if (!doc || !Array.isArray(doc.content)) return '';

  return doc.content
    .map((no) => (no.type === 'table' ? tabelaParaMarkdown(no) : textoDoNo(no)))
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/** Dispara o download de um arquivo texto no navegador. */
export const baixarArquivoTexto = (nomeArquivo, conteudo, mime = 'text/markdown;charset=utf-8') => {
  const blob = new Blob([conteudo], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Nome de arquivo seguro a partir do título da página. */
export const nomeArquivoSeguro = (titulo, extensao) =>
  `${String(titulo || 'documento')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase()
    .slice(0, 60) || 'documento'}.${extensao}`;

// ------------------------------------------------------- Árvore de páginas

/**
 * Monta a árvore de páginas a partir da lista plana da API.
 * Itens cujo pai não está na lista (ex: filtro por categoria) viram raiz,
 * para nunca sumirem da navegação.
 */
export const montarArvore = (itens = []) => {
  const porId = new Map(itens.map((i) => [i.id, { ...i, filhos: [] }]));
  const raizes = [];

  porId.forEach((no) => {
    const pai = no.parentId ? porId.get(no.parentId) : null;
    if (pai && pai.id !== no.id) pai.filhos.push(no);
    else raizes.push(no);
  });

  const ordenar = (lista) => {
    lista.sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || String(a.title).localeCompare(String(b.title)));
    lista.forEach((n) => ordenar(n.filhos));
  };
  ordenar(raizes);

  return raizes;
};

/** Caminho da raiz até o item (breadcrumb). */
export const caminhoDoItem = (itens = [], id) => {
  const porId = new Map(itens.map((i) => [i.id, i]));
  const caminho = [];
  let atual = porId.get(id);
  let saltos = 0;

  while (atual && saltos < 50) {
    caminho.unshift(atual);
    atual = atual.parentId ? porId.get(atual.parentId) : null;
    saltos++;
  }

  return caminho;
};

/** IDs do item e de todos os descendentes — usado para bloquear ciclos no select de pai. */
export const idsDescendentes = (itens = [], id) => {
  const ids = new Set([id]);
  let mudou = true;

  while (mudou) {
    mudou = false;
    itens.forEach((i) => {
      if (i.parentId && ids.has(i.parentId) && !ids.has(i.id)) {
        ids.add(i.id);
        mudou = true;
      }
    });
  }

  return ids;
};
